const VALID_EXECUTE_FIELD_IN = new Set(["path", "query", "body", "formData", "header", "cookie"]);

export const normalizeFieldIn = (raw) => {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const low = s.toLowerCase();
  if (low === "formdata") return "formData";
  if (VALID_EXECUTE_FIELD_IN.has(s)) return s;
  if (VALID_EXECUTE_FIELD_IN.has(low)) return low === "formdata" ? "formData" : low;
  return undefined;
};

export const fieldInFromDict = (f) => {
  if (!f || typeof f !== "object") return undefined;
  return normalizeFieldIn(f.in ?? f.paramIn ?? f.schema?.in);
};

export const resolveFieldIn = (proposedField, catalogField) =>
  fieldInFromDict(proposedField) || fieldInFromDict(catalogField) || "path";

export const catalogFieldsByName = (action) => {
  const fields = action?.config?.fields || action?.fields || [];
  const map = {};
  if (!Array.isArray(fields)) return map;
  for (const f of fields) {
    const name = f?.name || f?.title;
    if (!name) continue;
    map[name] = f;
    map[String(name).toLowerCase()] = f;
  }
  return map;
};

const catalogBodyParent = (field) => {
  if (!field || typeof field !== "object") return null;
  const name = field.name || field.title;
  if (!name) return null;
  const inv = fieldInFromDict(field);
  const schema = field.schema || {};
  if ((inv == null || inv === "formData" || inv === "body" || name === "body") && schema.type === "object") {
    return String(name);
  }
  return null;
};

const parseObjectValue = (val) => {
  if (val == null) return null;
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    const s = val.trim();
    if (!s.startsWith("{") && !s.startsWith("[")) return null;
    try {
      const parsed = JSON.parse(s);
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Expand RJSF body object fields for brain TOOLS (workflow-style dotted keys).
 */
export const expandBrainToolFields = (fields = []) => {
  const out = [];
  for (const f of fields) {
    if (!f || typeof f !== "object") continue;
    const name = f.name || f.title;
    if (!name) continue;
    const parent = catalogBodyParent(f);
    if (parent) {
      const bodyIn = resolveFieldIn(f, f);
      const props = f.schema?.properties || {};
      Object.keys(props).forEach((propKey) => {
        const propSchema = props[propKey] || {};
        if (propSchema.hidden === true) return;
        out.push({
          name: `${parent}.${propKey}`,
          in: bodyIn,
          parent,
          property: propKey,
        });
      });
      continue;
    }
    out.push({ name, in: resolveFieldIn(f, f) });
  }
  return out;
};

/**
 * Merge body.* / flat body props into one formData body field before executeAction.
 */
export const coalesceExecuteFieldsForAction = (action, rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const catalog = catalogFieldsByName(action);
  let bodyField = null;
  let bodyIn = "formData";
  let bodyProps = new Set();

  Object.values(catalog).forEach((af) => {
    if (bodyField) return;
    const parent = catalogBodyParent(af);
    if (!parent) return;
    bodyField = parent;
    bodyIn = resolveFieldIn(af, af);
    const props = af.schema?.properties || {};
    bodyProps = new Set(Object.keys(props).map(String));
  });

  if (!bodyField) return rows;

  const bodyValues = {};
  const otherRows = [];

  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const name = String(row.name || row.title || "");
    if (!name) return;
    const inv = fieldInFromDict(row) || "path";
    const val = row.value;

    if (name.startsWith(`${bodyField}.`)) {
      bodyValues[name.split(".", 1)[1]] = val;
      return;
    }
    if (name === bodyField && (inv === "body" || inv === "formData")) {
      const obj = parseObjectValue(val);
      if (obj) Object.assign(bodyValues, obj);
      else if (val != null && val !== "") bodyValues[name] = val;
      return;
    }
    if (bodyProps.has(name) && (inv === "body" || inv === "formData" || inv === "path")) {
      bodyValues[name] = val;
      return;
    }
    otherRows.push(row);
  });

  if (Object.keys(bodyValues).length > 0) {
    const cat = catalog[bodyField] || catalog[String(bodyField).toLowerCase()];
    otherRows.push({
      name: bodyField,
      in: bodyIn,
      value: bodyValues,
      ...(cat?.schema ? { schema: cat.schema } : {}),
    });
  }

  return otherRows;
};

export const normalizeExecuteFieldRow = (proposedField, catalogField) => {
  if (!proposedField || typeof proposedField !== "object") return null;
  const name = proposedField.name || proposedField.title;
  if (!name) return null;
  let value = proposedField.value;
  // Recover if HTTP Request was stored as a JSON string.
  if (String(name).toLowerCase() === "http request" && typeof value === "string") {
    const s = value.trim();
    if (s.startsWith("{") && s.endsWith("}")) {
      try {
        const parsed = JSON.parse(s);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) value = parsed;
      } catch {
        /* keep string */
      }
    }
  }
  const row = {
    name,
    in: resolveFieldIn(proposedField, catalogField),
    value,
    ...(catalogField?.schema ? { schema: catalogField.schema } : {}),
  };
  if (proposedField.required === true || catalogField?.required === true) {
    row.required = true;
  } else if (String(name).toLowerCase() === "http request") {
    row.required = true;
  }
  return row;
};

export const normalizeExecuteFieldsForAction = (action, proposedFields) => {
  const catalog = catalogFieldsByName(action);
  const rows = [];

  if (Array.isArray(proposedFields) && proposedFields.length > 0) {
    proposedFields.forEach((f) => {
      if (!f || typeof f !== "object") return;
      const name = f.name || f.title;
      if (!name) return;
      const cat = catalog[name] || catalog[String(name).toLowerCase()];
      const row = normalizeExecuteFieldRow(f, cat);
      if (row) rows.push(row);
    });
    return coalesceExecuteFieldsForAction(action, rows);
  }

  const configFields = action?.config?.fields || action?.fields || [];
  if (!Array.isArray(configFields)) return rows;

  configFields.forEach((af) => {
    if (!af || typeof af !== "object") return;
    const name = af.name || af.title;
    if (!name) return;
    const val = af.value;
    if (val == null || val === "" || String(val) === "Let agent pick value") return;
    const row = normalizeExecuteFieldRow({ name, value: val }, af);
    if (row) rows.push(row);
  });

  return coalesceExecuteFieldsForAction(action, rows);
};

export const serializeExecuteFieldValue = (name, inv, rawVal, catalogField) => {
  if (rawVal == null) return "";
  const location = inv || "path";
  const schemaType = catalogField?.schema?.type;
  const nameLow = String(name || "").toLowerCase();
  // Custom Request "HTTP Request" (and other object-schema fields) must stay objects —
  // executeAction expects value: { requestType, url, stringBody }, not a JSON string.
  const isObjectField =
    schemaType === "object" || nameLow === "http request" || nameLow === "body";
  const isBodyContainer =
    location === "formData" ||
    (location === "body" && (name === "body" || schemaType === "object"));
  if (typeof rawVal === "object") {
    if (isBodyContainer || isObjectField) return rawVal;
    return JSON.stringify(rawVal);
  }
  return String(rawVal);
};
